import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Briefcase } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DanyDraftList from "@/components/dany/DanyDraftList";
import ViewNav from "@/components/nav/ViewNav";
import { useAuth } from "@/contexts/AuthContext";
import { jobDetailPath, newJobPath, ownJobsPath } from "@/lib/ownerRoutes";
import { from } from "@/lib/cameFrom";
import api from "@/lib/api";
import { formatDate } from "@/lib/dates";

/**
 * The jobs this account posted. Mounted twice since DY2.6 — at `/client/jobs`
 * and at `/provider/requests` — because `GET /jobs?mine=true` was never a
 * client query: it is scoped by ownership, and a FixyBee owns jobs now.
 *
 * Nothing here branches on the role except the links out of it, which have to
 * stay inside the caller's own namespace or `ProtectedRoute` throws them out.
 */
export default function ClientJobs() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.profile?.role;
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);

  // Same defect as ProviderQuotes and the same fix (DY2.2 V4): `setLoading`
  // lived on the success path only, so a rejected request left this page
  // animating skeletons forever. `finally` owns the flag; the failure is a
  // state that renders.
  useEffect(() => {
    let live = true;
    setLoading(true);
    setFailed(false);
    const params = new URLSearchParams({ mine: "true" });
    if (status) params.set("status", status);
    api.get(`/jobs?${params}`)
      .then((r) => { if (live) setJobs(r.data.jobs || []); })
      .catch(() => { if (live) setFailed(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [status, reload]);

  const filters = [
    { v: "", label: t("clientJobsX.filters.all") },
    { v: "open", label: t("clientJobsX.filters.open") },
    { v: "in_progress", label: t("clientJobsX.filters.in_progress") },
    { v: "completed", label: t("clientJobsX.filters.completed") },
    { v: "canceled", label: t("clientJobsX.filters.canceled") },
  ];

  const statusLabel = (s) => ({
    open: t("clientJobsX.statusOpen"),
    in_progress: t("clientJobsX.statusInProgress"),
    completed: t("clientJobsX.statusCompleted"),
    canceled: t("clientJobsX.statusCanceled"),
  }[s] || s);

  return (
    <DashboardLayout role={role}>
      <div data-testid="client-jobs-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">
            {role === "provider" ? t("providerRequests.title") : t("clientJobsX.title")}
          </h1>
          <Link to={newJobPath(role)} className="fx-btn fx-btn--primary" data-testid="post-job-cta"><Plus size={18} /> {t("clientJobsX.postCta")}</Link>
        </div>

        {/* One scrolling line below 992px instead of three wrapped rows of
            pills (T13-M3). Same filters, same words, same order. */}
        <ViewNav
          className="mb-6"
          testId="status-filters"
          items={filters.map((f) => ({
            key: f.v, label: f.label, testId: `filter-${f.v || "all"}`,
          }))}
          active={status}
          onSelect={setStatus}
        />

        {/* N36. The screen a person opens to see their jobs is this one, not
            the overview, and until now a job Dany had written with them and
            they had not published yet was on neither — it lived in the
            conversation and nowhere else, so finding it meant remembering
            which conversation. A client with one draft and no published job
            was shown "Aún sin trabajos" over work that existed.

            Only under "all". A draft has no job status, so surfacing it under
            "Completados" would be putting it in a bucket it is not in; the
            unfiltered view is the one that promises to show everything. */}
        {!status && <DanyDraftList className="mb-6" />}

        {loading ? (
          <div className="space-y-3">{Array(3).fill(0).map((_, i) => <div key={i} className="skeleton h-32" />)}</div>
        ) : failed ? (
          <div className="card-base p-10 text-center" data-testid="client-jobs-error">
            <Briefcase size={36} className="mx-auto text-smoke" />
            <p className="mt-3 text-smoke">{t("common.loadFailed")}</p>
            <button className="fx-btn fx-btn--secondary mt-4" onClick={() => setReload((n) => n + 1)}
                    data-testid="client-jobs-retry">{t("common.retry")}</button>
          </div>
        ) : jobs.length === 0 ? (
          <div className="card-base p-10 text-center">
            <Briefcase size={36} className="mx-auto text-smoke" />
            {/* Its own copy for a FixyBee: "Aún sin trabajos" on the page a
                professional opens reads as "there is no work available", which
                is the opposite of what this page is about. */}
            <h3 className="mt-4 font-display text-xl font-bold text-worker">
              {t(role === "provider" ? "providerRequests.emptyTitle" : "clientJobsX.emptyTitle")}
            </h3>
            <p className="text-smoke mt-1">
              {t(role === "provider" ? "providerRequests.emptyBody" : "clientJobsX.emptyBody")}
            </p>
            <Link to={newJobPath(role)} className="fx-btn fx-btn--secondary mt-6"><Plus size={18} /> {t("clientJobsX.postCta")}</Link>
          </div>
        ) : (
          <div className="grid grid-cols-[minmax(0,1fr)] gap-3" data-testid="jobs-list">
            {/* The column is `minmax(0,1fr)` and not a bare `grid` (T13-M2).
                A bare grid has one IMPLICIT track, sized `auto`, and `auto`
                floors at min-content — which here is the card's `truncate`
                title unwrapped, 388px of it. The track held that width, the
                page grew to 462 on a 360px phone, and everything right of the
                fold was cut, the tab bar included. `minmax(0, ...)` is the
                idiom ds/dany/DanyProse.jsx already uses for this. The truncate
                itself is untouched — it is what Design specified, and with a
                column that can shrink it finally does its job. */}
            {jobs.map((j) => (
              <Link key={j.job_id} to={jobDetailPath(role, j.job_id)}
                    state={from(ownJobsPath(role))}
                    className="card-base p-5 block" data-testid={`job-card-${j.job_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-lg font-bold text-worker truncate">{j.title}</h3>
                    <p className="text-sm text-smoke line-clamp-2 mt-1">{j.description}</p>
                  </div>
                  <span className={`badge whitespace-nowrap ${
                    j.status === "open" ? "bg-hive text-worker" :
                    j.status === "in_progress" ? "bg-info text-cream" :
                    j.status === "completed" ? "bg-success text-cream" :
                    "bg-sand"
                  }`}>{statusLabel(j.status)}</span>
                </div>
                <div className="mt-3 flex gap-4 text-xs text-smoke font-mono">
                  <span>${j.budget_min}-${j.budget_max}</span>
                  <span>{j.location?.city}{j.location?.state ? `, ${j.location.state}` : ""}</span>
                  <span>{formatDate(j.created_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
